import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useStore } from "@/state/store";
import type {
  ArchiveDetail,
  ArchiveScope,
  ArchiveSession,
} from "@offsite/shared";

const SCOPE_LABEL: Record<ArchiveScope, string> = {
  all: "Komplett",
  topics: "Themen",
  votes: "Stimmen",
  teams: "Teams",
  requirements: "Anforderungen",
  evaluations: "Bewertungen",
  outputs: "Outputs",
  phase: "Phase",
  gruppe: "Besuchergruppe",
};

/**
 * Themen-Export für die Nachbereitung: alle eingereichten Anforderungen —
 * laufende Runde plus jede archivierte Gruppe — als CSV. Semikolon und BOM,
 * damit deutschsprachiges Excel die Datei per Doppelklick richtig öffnet.
 */
async function themenExportieren(token: string | null): Promise<void> {
  const { zeilen } = await api.admin.export(token);
  const spalten = [
    "Gruppe", "Archiviert am", "Team", "Name", "Abteilung", "Rolle", "Status",
    "Was soll das Werkzeug sein (thema)", "Was muss es können (was)",
    "Für wen", "Problem", "Worauf kommt es an (anforderungen)",
    "Einschränkungen", "Beispiel", "Ergebnis-Link",
  ];
  const feld = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const rows = zeilen.map((z) =>
    [
      z.gruppe,
      z.archiviertAm ? new Date(z.archiviertAm).toLocaleString("de-DE") : "",
      z.teamNumber,
      z.name ?? "",
      z.abteilung ?? "",
      z.rolle ?? "",
      z.status,
      z.thema,
      z.was,
      z.fuerWen,
      z.problem,
      z.anforderungen,
      z.einschraenkungen,
      z.beispiel,
      z.resultId ? `${window.location.origin}/ergebnis/${z.resultId}` : "",
    ]
      .map(feld)
      .join(";"),
  );
  const csv = "﻿" + [spalten.map(feld).join(";"), ...rows].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `strategietag-themen-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function AdminArchiveSection() {
  const { state } = useStore();
  const [archives, setArchives] = useState<ArchiveSession[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ArchiveDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    try {
      const r = await api.admin.archives(state.auth.token);
      setArchives(r.archives);
    } catch (err) {
      console.error(err);
      setError("Konnte Archive nicht laden.");
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.auth.token]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.admin
      .archive(state.auth.token, selectedId)
      .then((r) => {
        if (!cancelled) setDetail(r.archive);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setError("Konnte Detail nicht laden.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, state.auth.token]);

  return (
    <div className="admin-grid">
      <header className="admin-section-head">
        <h2>Archiv</h2>
        <button
          type="button"
          className="btn"
          data-testid="themen-export"
          onClick={() => void themenExportieren(state.auth.token).catch(console.error)}
          title="Alle eingereichten Anforderungen (laufende Runde + alle Gruppen) als CSV für die Nachbereitung"
        >
          Themen exportieren (CSV)
        </button>
        <p>
          Alle archivierten Workshop-Stände — read-only. Klick auf ein Archiv
          öffnet die Detail-Ansicht.
        </p>
      </header>

      {error && <div className="admin-callout">{error}</div>}

      <div className="admin-archive-layout">
        <div className="admin-archive-list">
          {archives.length === 0 && (
            <p className="form-hint">Noch keine Archive.</p>
          )}
          {archives.map((a) => {
            const active = a.archiveId === selectedId;
            return (
              <button
                key={a.archiveId}
                type="button"
                className={`admin-archive-card${active ? " is-active" : ""}`}
                onClick={() => setSelectedId(a.archiveId)}
              >
                <div className="admin-archive-card-row">
                  <strong>{fmtTs(a.archivedAt)}</strong>
                  <span className={`archive-scope-pill scope-${a.scope}`}>
                    {SCOPE_LABEL[a.scope]}
                  </span>
                </div>
                {a.label && (
                  <div className="admin-archive-card-label">„{a.label}"</div>
                )}
                <div className="admin-archive-card-meta">
                  {a.itemCount} Items · <code>{a.archiveId}</code>
                </div>
              </button>
            );
          })}
        </div>

        <div className="admin-archive-detail">
          {!selectedId && (
            <p className="form-hint">Wähle ein Archiv links für die Detail-Ansicht.</p>
          )}
          {selectedId && loading && <p className="form-hint">Lade …</p>}
          {selectedId && detail && <ArchiveDetailView detail={detail} />}
        </div>
      </div>
    </div>
  );
}

function ArchiveDetailView({ detail }: { detail: ArchiveDetail }) {
  const tallyByTopic = new Map(detail.voteTallies.map((t) => [t.topicId, t.count]));
  const evalByTeam = new Map(detail.evaluations.map((e) => [e.teamNumber, e]));
  const reqByTeam = new Map(
    detail.teamRequirements.map((r) => [r.teamNumber, r]),
  );
  const outputByTeam = new Map(detail.outputs.map((o) => [o.teamNumber, o]));

  return (
    <div className="admin-archive-detail-pane">
      <div className="admin-archive-detail-head">
        <h3 style={{ margin: 0 }}>{fmtTs(detail.archivedAt)}</h3>
        <span className={`archive-scope-pill scope-${detail.scope}`}>
          {SCOPE_LABEL[detail.scope]}
        </span>
        {detail.label && <span className="archive-label">„{detail.label}"</span>}
      </div>

      {detail.phaseAtArchive && (
        <section className="archive-section">
          <h4>Phase zum Archivierungszeitpunkt</h4>
          <p>
            <code>{detail.phaseAtArchive.phase}</code>
          </p>
        </section>
      )}

      {detail.topics.length > 0 && (
        <section className="archive-section">
          <h4>Themen ({detail.topics.length})</h4>
          <ul>
            {detail.topics.map((t) => {
              const votes = tallyByTopic.get(t.id) ?? 0;
              return (
                <li key={t.id}>
                  <strong>{t.title}</strong> — {votes} Stimmen
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {detail.teams.length > 0 && (
        <section className="archive-section">
          <h4>Teams ({detail.teams.length})</h4>
          {detail.teams.map((team) => {
            const req = reqByTeam.get(team.number);
            const ev = evalByTeam.get(team.number);
            const out = outputByTeam.get(team.number);
            return (
              <div key={team.number} className="archive-team-card">
                <div className="archive-team-head">
                  <strong>
                    Team {team.number}
                    {team.name ? ` — ${team.name}` : ""}
                  </strong>
                  {ev && (
                    <span className="archive-score">
                      {ev.score.toFixed(1)}
                      <small>/10</small>
                    </span>
                  )}
                </div>
                {req && (
                  <details>
                    <summary>Anforderung ({req.status})</summary>
                    <dl className="archive-req">
                      {(Object.entries(req.fields) as [string, string][]).map(
                        ([k, v]) => (
                          <div key={k}>
                            <dt>{k}</dt>
                            <dd>{v || "—"}</dd>
                          </div>
                        ),
                      )}
                    </dl>
                  </details>
                )}
                {ev && (
                  <div className="archive-eval">
                    <p>
                      <em>{ev.zusammenfassung}</em>
                    </p>
                    <div className="archive-eval-cols">
                      <div>
                        <strong>Gut</strong>
                        <ul>
                          {ev.gut.map((g, i) => (
                            <li key={i}>{g}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <strong>Ausbaufähig</strong>
                        <ul>
                          {ev.ausbaufaehig.map((g, i) => (
                            <li key={i}>{g}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                {out && (
                  <details>
                    <summary>
                      Output ({out.contentType}, {out.content.length} Zeichen)
                    </summary>
                    <pre className="archive-output-preview">
                      {out.content.slice(0, 1500)}
                      {out.content.length > 1500 ? "\n…" : ""}
                    </pre>
                  </details>
                )}
              </div>
            );
          })}
        </section>
      )}

      {detail.votes.length > 0 && detail.teams.length === 0 && (
        <section className="archive-section">
          <h4>Stimmen ({detail.votes.length})</h4>
          <p className="form-hint">
            Stimmen ohne Team-Zuordnung — siehe Themen-Liste oben für Tallies.
          </p>
        </section>
      )}
    </div>
  );
}

function fmtTs(ts: number): string {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
